import { Button } from "@datapay-by-datacom/design-system";
import {
  OrgUserSummary,
  UserOrgRoleSummary,
} from "../../../generated/core-api";
import { useUserManagementPermissions } from "../../hooks";
import s from "./UserDetailPanel.module.css";

interface UserDetailPanelProps {
  user: OrgUserSummary;
  onClose: () => void;
  onGrantAccess: () => void;
  onRevoke: (orgRole: UserOrgRoleSummary) => void;
  revokingKeys: Set<string>;
}

export function UserDetailPanel({
  user,
  onClose,
  onGrantAccess,
  onRevoke,
  revokingKeys,
}: UserDetailPanelProps) {
  const { canEdit } = useUserManagementPermissions();

  return (
    <aside className={s.panel} aria-label="User details">
      <div className={s.header}>
        <h3 className={s.title}>{user.name || "Unknown User"}</h3>
        <button
          type="button"
          className={s.closeButton}
          onClick={onClose}
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      <dl className={s.details}>
        <dt>Email</dt>
        <dd>{user.email || "-"}</dd>
        <dt>User ID</dt>
        <dd className={s.userId}>{user.user_id}</dd>
      </dl>

      <div className={s.section}>
        <div className={s.sectionHeader}>
          <h4 className={s.sectionTitle}>Org Memberships</h4>
          {canEdit && (
            <Button variant="secondary" onPress={onGrantAccess}>
              Grant Access
            </Button>
          )}
        </div>

        {user.org_roles.length === 0 ? (
          <p className={s.emptyMessage}>No org memberships</p>
        ) : (
          <ul className={s.orgList}>
            {user.org_roles.map((orgRole) => {
              const key = `${user.user_id}-${orgRole.org_id}-${orgRole.role}`;
              const isRevoking = revokingKeys.has(key);
              return (
                <li key={key} className={s.orgItem}>
                  <div className={s.orgInfo}>
                    <span className={s.orgName}>
                      {orgRole.org_name || orgRole.org_id}
                    </span>
                    <span
                      className={`${s.roleBadge} ${orgRole.role === "admin" ? s.roleBadgeAdmin : ""}`}
                    >
                      {orgRole.role}
                    </span>
                    <span className={s.orgCategory}>{orgRole.org_category}</span>
                  </div>
                  {canEdit && (
                    <button
                      className={s.revokeButton}
                      onClick={() => onRevoke(orgRole)}
                      disabled={isRevoking}
                      aria-label={`Revoke ${orgRole.org_name || orgRole.org_id} access`}
                    >
                      {isRevoking ? "Revoking..." : "Revoke"}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
